/**
 * Onboarding Screen 5: Create First Focus
 * Allow user to create their actual first Focus
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, CheckCircle, Loader, Sparkles } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

interface CreateFirstFocusProps {
  onComplete: () => void;
}

export const CreateFirstFocus: React.FC<CreateFirstFocusProps> = ({ onComplete }) => {
  const [focusText, setFocusText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!focusText.trim()) {
      setError('Please enter something for Swirl to think about');
      return;
    }

    if (!user) {
      setError('Please log in to create a Focus');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      // Import the focus service dynamically to avoid circular dependencies
      const { default: focusService } = await import('../../../services/focusService');
      
      const newFocus = await focusService.createFocus(user.id, {
        title: focusText.trim(),
        status: 'active',
        created_at: new Date().toISOString()
      });

      // Show success animation
      setShowSuccess(true);

      // Wait for animation, then close and navigate
      setTimeout(() => {
        onComplete();
        // Navigate to the new Focus after a brief delay
        setTimeout(() => {
          window.location.href = `/focus/${newFocus.id}`;
        }, 300);
      }, 2000);
    } catch (err: any) {
      console.error('Error creating Focus:', err);
      setError(err.message || 'Failed to create Focus. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 p-8">
      <AnimatePresence mode="wait">
        {!showSuccess ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="w-full max-w-2xl"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 mb-4"
              >
                <Target className="w-8 h-8 text-white" />
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-bold text-gray-900 mb-3"
              >
                Create your first Focus
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-lg text-gray-600"
              >
                What is one thing you want Swirl to think about for you right now?
              </motion.p>
            </div>

            {/* Form */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div>
                <label htmlFor="focus-input" className="sr-only">
                  Your Focus
                </label>
                <input
                  id="focus-input"
                  type="text"
                  value={focusText}
                  onChange={(e) => setFocusText(e.target.value)}
                  placeholder="e.g., Launch my side project, Learn Spanish, Get in shape..."
                  className="w-full px-6 py-4 text-lg rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all bg-white shadow-sm"
                  disabled={isCreating}
                  autoFocus
                />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                >
                  {error}
                </motion.div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isCreating || !focusText.trim()}
                  className="flex-1 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Create my Focus
                    </>
                  )}
                </button>
              </div>

              <button
                type="button"
                onClick={onComplete}
                className="w-full text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
                disabled={isCreating}
              >
                Skip for now
              </button>
            </motion.form>

            {/* Helpful Tips */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-8 p-6 bg-white/50 backdrop-blur-sm rounded-xl border border-purple-100"
            >
              <p className="text-sm text-gray-600 text-center">
                💡 <strong>Tip:</strong> Swirl works best with specific goals. The more concrete your Focus, the better Swirl can help you achieve it.
              </p>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 mb-6"
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold text-gray-900 mb-3"
            >
              Focus created!
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-gray-600"
            >
              Swirl is already thinking about it...
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

